/**
 * Guard for the Vercel Analytics beacon.
 *
 * Two separate incidents, one hook. Both were invisible in the Vercel
 * dashboard until someone pulled the day-level rows by hand.
 *
 * 1. THE LOOP. On 2026-06-26 a single client emitted 30,749 page views on
 *    /playlist-generator from 19 visitors, peaking at 6,182 in one hour and
 *    running for nine hours before it stopped. Normal is under 10 per
 *    session. That one day then dominated the 30-day comparison badge, which
 *    read "Page Views -74%" for two months while real page views were up
 *    about 22%. Nothing errored, and no counter anywhere said a client was
 *    stuck.
 *
 * 2. THE CRAWLERS. src/lib/analytics.ts already refuses to write crawler
 *    sessions into analytics_events (see isLikelyBot for that incident), but
 *    the Vercel beacon is a separate pipeline and was never covered. On
 *    2026-08-22, 61 "visitors" on Chrome/GNU-Linux arrived inside four hours
 *    against 1 in the same window two days earlier, each reading a single
 *    long-tail blog post with no referrer. Vercel counted every one.
 *
 * The budget is the part worth explaining. It is a hard ceiling per page
 * load, not a rate limit, because a rate limit still lets a nine-hour loop
 * through at whatever rate it allows - 1/minute for 9 hours is still 540
 * events. A total cap is what makes the incident shape impossible. 200 sits
 * far above any plausible human (that is 200 SPA navigations without a
 * reload) and far below the 30,749 that actually happened.
 *
 * Reaching the ceiling fires ONE first-party diagnostic event, so a loop is
 * observable the same day instead of nine hours later in a screenshot. That
 * is the falsifiable signal: without it, "no runaway page views" and "the
 * guard silently ate a real user's session" look identical.
 *
 * 2026-08-27: THE BOT GATE NO LONGER DROPS, IT ONLY REPORTS. Dropping on
 * isLikelyBot took Vercel Analytics from 107 visitors on 08-25 to 4 on 08-26,
 * a 96% fall, measured through Vercel's API rather than the dashboard. The
 * hourly series pins the cutoff to 19:00-20:00 UTC on 08-25, which is when
 * this guard shipped.
 *
 * Real traffic did not move: first-party human sessions ran 22, 19, 26, 28,
 * 24 across those days and 08-26 carried 88 real playlist generations.
 *
 * The honest problem was that a dropped crawler and a dropped person looked
 * identical from outside, so neither 107 nor 4 could be trusted. The gate now
 * lets the beacon through and fires ONE diagnostic per page load instead, so
 * the share that is genuinely automated becomes a number we can read before
 * deciding whether to drop again. The BUDGET still drops, because that is the
 * half that actually stopped the 30,749-pageview incident, and its failure
 * mode is bounded where the bot gate's was total.
 */
import { isLikelyBot, trackEvent } from "@/lib/analytics";

/**
 * Vercel Analytics events allowed per page load, across page views and
 * custom events. See the module comment for why this is a total and not a
 * rate.
 */
export const VERCEL_EVENT_BUDGET = 200;

/**
 * How long to wait for the FIRST beacon before calling the pipeline dead.
 *
 * The script is injected with `defer`, so it parses after the document and
 * then posts. 10s is far past that on any connection worth measuring, and the
 * watchdog is cancelled on unmount, so a visitor who leaves sooner never
 * reports.
 */
export const BEACON_WATCHDOG_MS = 10_000;

interface GuardOptions {
  budget?: number;
  /** Injected in tests. Defaults to the shared first-party predicate. */
  isBot?: () => boolean;
  /** Injected in tests. Fires once, when the budget is exhausted. */
  onBudgetExhausted?: (sent: number) => void;
  /** Injected in tests. Fires once per page load when the visitor looks
   *  automated. Reports only: it does not suppress the beacon. */
  onBotSuspected?: (userAgent: string) => void;
  /** Called as each event is allowed through. The module singleton uses this
   *  to answer "did anything ever reach Vercel on this page load". */
  onSend?: () => void;
}

/**
 * Whether the app-wide guard has passed at least one event this page load.
 *
 * WHY THIS EXISTS. The 2026-08-25 outage reported zero pageviews for two days
 * and NOTHING observed it: the script returned 200, window.va was a function,
 * no error was raised, and the dashboard drew a clean zero that reads as "no
 * visitors" rather than "not reporting". It was found by a person looking at a
 * chart.
 *
 * The failure is directly observable in the browser: if a beacon is ever
 * attempted, `beforeSend` runs. If it never runs, nothing was sent. That is a
 * better signal than reconciling counts against Vercel's API after the fact,
 * because it needs no credential, no undocumented endpoint, and it observes
 * the defect itself rather than inferring it from a number.
 */
let singletonEverSent = false;
export function vercelBeaconEverSent(): boolean {
  return singletonEverSent;
}

/** Test-only: reset the page-load flag. */
export function resetVercelBeaconSentFlag(): void {
  singletonEverSent = false;
}

/**
 * Classify a page load where no beacon was attempted.
 *
 * The two causes are indistinguishable from the count alone and need
 * different responses, so the diagnostic carries which one it was:
 *
 *   `script_absent`  window.va is missing, so the script never ran. Ad
 *                    blocker, a network failure, or Web Analytics disabled on
 *                    the project. Expected at some background rate and NOT
 *                    our bug.
 *   `never_invoked`  the script IS loaded and beforeSend still never ran. That
 *                    is the 2026-08-25 shape exactly, and it should be zero.
 *
 * Without this split the metric is uninterpretable, because ad-blocked
 * visitors would swamp it and the real defect would look like background noise.
 */
export type BeaconSilenceKind = "script_absent" | "never_invoked";

export function classifyBeaconSilence(hasVaGlobal: boolean): BeaconSilenceKind {
  return hasVaGlobal ? "never_invoked" : "script_absent";
}

/**
 * Report a stuck client through the first-party pipeline rather than the
 * Vercel one, so the report cannot itself be swallowed by the budget it is
 * reporting on. skipDataLayer keeps a diagnostic out of GA4.
 */
function reportBudgetExhausted(sent: number): void {
  trackEvent(
    "pageview_budget_exhausted",
    "diagnostics",
    typeof window !== "undefined" ? window.location.pathname : undefined,
    { budget: sent },
    { skipDataLayer: true },
  );
}

/**
 * Report a suspected automated visitor WITHOUT suppressing its beacon.
 *
 * Through the first-party pipeline on purpose: a diagnostic about the Vercel
 * beacon must not travel on the Vercel beacon, or a total failure of that
 * pipeline reports nothing about itself.
 */
function reportBotSuspected(userAgent: string): void {
  trackEvent(
    "vercel_beacon_bot_suspected",
    "diagnostics",
    typeof window !== "undefined" ? window.location.pathname : undefined,
    { userAgent: userAgent.slice(0, 200) },
    { skipDataLayer: true },
  );
}

/**
 * Build a beforeSend handler. Exported for tests; the app uses the
 * module-level singleton below, whose counter is scoped to the page load
 * because the module is re-evaluated on every real navigation.
 *
 * Generic in the event type so this file does not have to import
 * BeforeSendEvent from @vercel/analytics - the handler is pass-through or
 * null and never inspects the event, so the package's own type flows
 * through unchanged at the call site.
 */
export function createVercelBeforeSend(options: GuardOptions = {}) {
  const {
    budget = VERCEL_EVENT_BUDGET,
    isBot = isLikelyBot,
    onBudgetExhausted = reportBudgetExhausted,
    onBotSuspected = reportBotSuspected,
    onSend,
  } = options;

  let sent = 0;
  let botVerdict: boolean | null = null;
  let reported = false;
  let botReported = false;

  return <E>(event: E): E | null => {
    // The user agent cannot change during a page load, so ask once. null is
    // "not yet asked" and is deliberately distinct from false, which is a
    // real answer.
    // === true on purpose: an inconclusive predicate must not collapse onto
    // "bot". src/lib/analytics.ts is deliberately conservative here, because
    // a missed bot costs some noise while a false positive silently deletes
    // a real user's whole session. Failing OPEN is the correct default in
    // this one place, so it is stated rather than left to coercion.
    if (botVerdict === null) botVerdict = isBot() === true;
    // REPORTS, does not drop. See the 2026-08-27 note above: dropping here
    // cost 96% of the beacon and made the two failure modes indistinguishable.
    if (botVerdict && !botReported) {
      botReported = true;
      try {
        onBotSuspected(
          typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
        );
      } catch {
        // A diagnostic must never break the page it is diagnosing.
      }
    }

    if (sent >= budget) {
      if (!reported) {
        reported = true;
        try {
          onBudgetExhausted(sent);
        } catch {
          // A diagnostic must never break the page it is diagnosing.
        }
      }
      return null;
    }

    sent += 1;
    // Signalled AFTER the budget check, so a page load that only ever hit the
    // ceiling is not counted as having reached Vercel. try/catch because a
    // diagnostic must never break the page it is diagnosing.
    try {
      onSend?.();
    } catch {
      // ignored
    }
    return event;
  };
}

/**
 * The instance the app passes to <Analytics beforeSend={...} />. Must be a
 * stable reference: creating it inside JSX would rebuild the closure on
 * every render and reset the counter, which is the one way to write this
 * guard so that it cannot fire.
 */
export const vercelBeforeSend = createVercelBeforeSend({
  onSend: () => {
    singletonEverSent = true;
  },
});

/**
 * Report that this page load attempted no beacon at all.
 *
 * Through the FIRST-PARTY pipeline, necessarily: a diagnostic about the Vercel
 * beacon cannot travel on the Vercel beacon, or the total failure it exists to
 * report is the one case where it says nothing.
 */
function reportBeaconSilent(kind: BeaconSilenceKind, waitedMs: number): void {
  trackEvent(
    "vercel_beacon_silent",
    "diagnostics",
    typeof window !== "undefined" ? window.location.pathname : undefined,
    { kind, waitedMs },
    { skipDataLayer: true },
  );
}

/**
 * Start the watchdog for one page load. Returns its cancel function.
 *
 * Deliberately NOT a hook and NOT importing React, so it is testable with fake
 * timers and no renderer. App.tsx owns the effect that calls it.
 *
 * `report` and `hasVaGlobal` are injected so a test never depends on a real
 * timer, a real window, or the real analytics pipeline.
 */
export function startBeaconWatchdog(opts: {
  delayMs?: number;
  everSent?: () => boolean;
  hasVaGlobal?: () => boolean;
  report?: (kind: BeaconSilenceKind, waitedMs: number) => void;
} = {}): () => void {
  const {
    delayMs = BEACON_WATCHDOG_MS,
    everSent = vercelBeaconEverSent,
    hasVaGlobal = () =>
      typeof window !== "undefined" &&
      typeof (window as { va?: unknown }).va === "function",
    report = reportBeaconSilent,
  } = opts;

  const timer = setTimeout(() => {
    if (everSent()) return;
    try {
      report(classifyBeaconSilence(hasVaGlobal()), delayMs);
    } catch {
      // A diagnostic must never break the page it is diagnosing.
    }
  }, delayMs);

  return () => clearTimeout(timer);
}
